import { Axios } from "api/axios";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import React, { Fragment, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  exportSalesOrderProductCSV,
  exportSalesOrderProductTallyCSV,
  fetchAllProductSalesOrders,
  fetchSalesOrderServiceByType
} from "slices/sales_order/thunk";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ExportDropdownButton from "Common/Components/Buttons/ExportDropdownButton";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import OrderTableContainer from "Common/Tables/OrderFiltersTable/OrderTableContainer";
import {
  defaultInnerStyle,
} from "Common/Components/PageContainer/PageContainer";
import DynamicTabs from "components/DynamicTabs";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { useResetTableOnTabChange } from "CustomHooks/useResetTableOnTabChange";
import { ClipboardList, FileText, Timer, Truck } from "lucide-react";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { Add } from "@mui/icons-material";
import { MecordColors } from "Constants/Color";
import SearchBox from "Common/Components/SearchBox/SearchBox";

interface MasterListPageProps {
  title: string;
  addButtonLabel: string;
  addRoute: string;
  fetchThunk: any; // thunk for fetching data
  exportThunk: any; // thunk for exporting data
  columns: any[];
  statusToggleUrl: string;
  activeTab?: string; // current active tab
  activeType?: string | undefined; // product or services
  // API url for status toggle
  handleDelete?: any;
  onDeleteComplete?: () => void; // Optional callback for when delete completes
  reloadFlag?: number; // Flag to trigger data reload
}

const MasterListPage: React.FC<MasterListPageProps> = ({
  title,
  addButtonLabel,
  addRoute,
  // fetchThunk,
  // exportThunk,
  columns,
  statusToggleUrl,
  activeTab,
  activeType,
  handleDelete,
  onDeleteComplete,
  reloadFlag,
}) => {
  document.title = "Sales Order";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // const selectData = createSelector(
  //   (state: any) => state.SalesOrder,
  //   (salesOrder) => ({
  //     list: salesOrder.salesOrderList,
  //     rowCount: salesOrder.rowCount,
  //     loading: salesOrder.loading,
  //     error: salesOrder.error,
  //   })
  // );

  // const { list, rowCount, loading, error } = useSelector(selectData);
  // console.log("List Data: line 46 in SalesOrder", list);

  const statuses = [
    {
      label: "Total Sales Orders",
      icon: <FileText size={16} />,
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
    },
    {
      label: "Active Orders",
      icon: <ClipboardList size={16} />,
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
    },
    {
      label: "Delivered Orders",
      icon: <Truck size={16} />,
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
    },
    {
      label: "Pending Delivery",
      icon: <Timer size={16} />,
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
    },
  ];

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.sales_order?.app_name,
    app_modules?.sales_order?.modules?.product
  );

  // sales_order_log || draft
  const tableState = usePreservedTableState({
    key: TableKeys.SALES_ORDER.PRODUCT,
    tabString: activeTab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  useResetTableOnTabChange(TableKeys.SALES_ORDER.PRODUCT, activeTab, resetState);



  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tableInstance, setTableInstance] = useState<any>();
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger state
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";


  // Fetch list - memoized to prevent infinite loops
  const handleFetchSalesOrderData = React.useCallback(async () => {
    setLoading(true);
    try {
      let resData; // Type the response structure
      // console.log("activeTab in line 142", activeTab)
      if (activeType === "product") {

        resData = await dispatch(
          fetchAllProductSalesOrders({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
            stage: activeTab === "sales_order_log" ? "orders_log" : (activeTab || ""),
          })
        ).unwrap();
      } else {
        resData = await dispatch(
          fetchSalesOrderServiceByType({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
            stage: activeTab === "sales_order_log" ? "orders_log" : (activeTab || ""),
          })
        ).unwrap();
      }
      // console.log("resData in line 132 quotation: ", resData);

      // 💡 KEY CHANGE: The data is already in the expected shape, so we set it directly.
      // console.log("resData in line 172", resData)
      // Backend response structure: { status, success, message, count, data: [...] }
      const responseData = resData?.data?.data || resData?.data || resData;
      setData(Array.isArray(responseData) ? responseData : []);
      setTotal(resData?.data?.count || resData?.count || 0);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, activeType, pageIndex, pageSize, sortField, sortDirection, search, filterValue, activeTab]);

  // The issue: handleDelete is a function reference that doesn't change when called.
  // When you call a function, its reference doesn't change - only when it's redefined.
  // Solution: Use a refreshTrigger state that increments to force re-fetch.
  // We'll expose a refresh function that parent can call, OR wrap handleDelete.

  // Create a wrapped version of handleDelete that triggers refresh after delete
  // This wrapped version should be used by the parent instead of the original
  const wrappedHandleDelete = React.useCallback(async (...args: any[]) => {
    if (handleDelete) {
      try {
        await handleDelete(...args);
        // Trigger refresh after successful delete by incrementing refreshTrigger
        setRefreshTrigger(prev => prev + 1);
        if (onDeleteComplete) {
          onDeleteComplete();
        }
      } catch (error) {
        // Error is already handled in handleDelete, just log here
        // console.error("Delete operation failed:", error);
        throw error; // Re-throw so parent can handle it if needed
      }
    }
  }, [handleDelete, onDeleteComplete]);

  // Expose refresh function via useImperativeHandle or just use the wrapped version
  // For now, we'll make wrappedHandleDelete available, but parent needs to use it
  React.useEffect(() => {
    // If handleDelete prop reference changes, it means parent recreated it
    // This won't help detect when it's called, but it's a fallback
  }, [handleDelete]);

  useEffect(() => {
    handleFetchSalesOrderData();
  }, [
    dispatch,
    search,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    filterValue,
    activeTab,
    refreshTrigger, // Use refreshTrigger instead of handleDelete
    reloadFlag, // Watch reloadFlag to trigger refresh
  ]);


  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Status toggle
  const handleStatusToggle = async (item: any) => {
    try {
      const updatedStatus = !item.is_active;
      const payload = { is_active: updatedStatus };
      setIsStatusUpdateLoading(true);

      await Axios.patch(`${statusToggleUrl}/${item.id}/update/`, payload);

      toast.success(
        `${title} ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      // dispatch(
      //   fetchThunk({
      //     page: pageIndex + 1,
      //     pageSize,
      //     sort: sortField,
      //     order: sortField ? sortDirection : "",
      //     search,
      //     filters: filterValue,
      //   })
      // );
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const exportStage =
    activeTab === "sales_order_log" ? "orders_log" : activeTab || "";

  // Export handler
  const handleExportCSV = async (): Promise<{ blob: Blob; filename: string }> => {
    return await exportSalesOrderProductCSV({
      search,
      filters: filterValue,
      stage: exportStage,
    });
  };

  const handleExportTally = async (): Promise<{ blob: Blob; filename: string }> => {
    return await exportSalesOrderProductTallyCSV({
      search,
      filters: filterValue,
      stage: exportStage,
    });
  };

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <div className="page-content px-3 mecord-kpi-list-page">
      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div className="mecord-list-panel" style={defaultInnerStyle}>
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>{title}</h6>
            {canCreate && (
              <Button
                variant="primary"
                onClick={() => navigate(addRoute)}
                className="d-flex align-items-center gap-2"
                style={{
                  backgroundColor: MecordColors.PrimaryButtonBGColor,
                  borderColor: MecordColors.PrimaryButtonBGColor,
                  borderRadius: "8px",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonHoverBGColor;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    MecordColors.PrimaryButtonBGColor;
                  e.currentTarget.style.borderColor =
                    MecordColors.PrimaryButtonBGColor;
                }}
              >
                <Add fontSize="small" />
                {addButtonLabel}
              </Button>
            )}
          </div>

          <div className="mecord-tabs-wrap">
            <DynamicTabs
              activeTab={activeTab}
              activeType={activeType}
              basePath="sales_order"
              tabs={[
                { value: "sales_order_log", label: "Order Log" },
                { value: "draft", label: "Draft" },
              ]}
            />
          </div>

          <Row className="align-items-center mecord-search-row g-2">
            <SearchBox
              value={searchInput}
              onChange={(e: any) => handleChange(e)}
              placeholder="Search..."
            />

            <Col
              xs={12}
              md={12}
              lg={8}
              xl={7}
              className="d-flex justify-content-end align-items-center gap-2"
            >
              {tableInstance && (
                <ColumnVisibilityDropdown
                  columns={tableInstance.getAllLeafColumns()}
                  excludedColumns={["action", "sr_no"]}
                />
              )}
              {canView &&
                (activeType === "product" && activeTab === "sales_order_log" ? (
                  <ExportDropdownButton
                    options={[
                      {
                        label: "Export CSV",
                        onExport: handleExportCSV,
                      },
                      {
                        label: "Export for Tally",
                        onExport: handleExportTally,
                      },
                    ]}
                  />
                ) : (
                  <ExportButton onExport={handleExportCSV} />
                ))}
            </Col>
          </Row>
        </div>

        <div
          style={{
            flex: "1 1 0",
            maxHeight: "100%",
            minHeight: 0,
            marginTop: 4,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LoaderErrorWrapper
            loading={loading || isStatusUpdateLoading}
            error={error}
          >
            <MasterTableContainer
              columns={columns}
              data={data}
              totalRecords={total}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              isGlobalFilter={false}
              SearchPlaceholder={`Search ${title.toLowerCase()}...`}
              manualSorting={true}
              colValue={setFilterValue}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        </div>
      </div>
    </div>
  );
};

export default MasterListPage;
